/**
 * Component: Bulk Order Module
 * -----------------------------------------------------------------------------
 * Functionality for bulk order page.
 *
 */

import { on } from '~/helpers/utils'
import collection from '~/graphql/queries/collection.gql'
import { formatBulkOrderCollection } from '~/helpers/format'
import { misc as icons } from '~/helpers/svg-map'
import { values as breakpoints } from '~/config/breakpoints'

/**
 * DOM selectors.
 */
const selectors = {
  addCard: '[js-bulk-order="addCard"]',
  addToCart: '[js-bulk-order="addToCart"]',
  addToCartWrap: '[js-bulk-order="addToCartWrap"]',
  addToOrder: '[js-bulk-order="addToOrder"]',
  colorSwatch: '[js-bulk-order="colorSwatch"]',
  colorSwatches: '[js-bulk-order="colorSwatches"]',
  compareAt: '[js-bulk-order="compareAt"]',
  compareAtWrap: '[js-bulk-order="compareAtWrap"]',
  discount: '[js-bulk-order="discount"]',
  frameSwatch: '[js-bulk-order="frameSwatch"]',
  lensSwatch: '[js-bulk-order="lensSwatch"]',
  lensTooltip: '[js-bulk-order="lensTooltip"]',
  modal: '[js-bulk-order="modal"]',
  modalBackground: '[js-bulk-order="modalBackground"]',
  modalInner: '[js-bulk-order="modalInner"]',
  modalStep: '[js-bulk-order="modalStep"]',
  modalStep1: '[js-bulk-order="modalStep1"]',
  productCard: '[js-bulk-order="productCard"]',
  productTrack: '[js-bulk-order="productTrack"]',
  quantityDecrease: '[js-bulk-order="quantityDecrease"]',
  quantityDecreaseLineItem: '[js-bulk-order="quantityDecreaseLineItem"]',
  quantityIncrease: '[js-bulk-order="quantityIncrease"]',
  quantityIncreaseLineItem: '[js-bulk-order="quantityIncreaseLineItem"]',
  quantityInput: '[js-bulk-order="quantityInput"]',
  quantityInputLineItem: '[js-bulk-order="quantityInputLineItem"]',
  quantityLineItem: 'js-bulk-order="quantityLineItem"',
  quantityModal: '[js-bulk-order="quantityModal"]',
  quantityWrap: '[js-bulk-order="quantityWrap"]',
  remove: '[js-bulk-order="remove"]',
  sidebarEmpty: '[js-bulk-order="sidebarEmpty"]',
  sidebarLineItems: '[js-bulk-order="sidebarLineItems"]',
  sidebarLineItemsWrap: '[js-bulk-order="sidebarLineItemsWrap"]',
  stepsWrap: '[js-bulk-order="stepsWrap"]',
  subtotal: '[js-bulk-order="subtotal"]',
  summaryColor: '[js-bulk-order="summaryColor"]',
  summaryColorSwatch: '[js-bulk-order="summaryColorSwatch"]',
  summaryImage: '[js-bulk-order="summaryImage"]',
  summaryTitle: '[js-bulk-order="summaryTitle"]',
  thresholdWarning: '[js-bulk-order="thresholdWarning"]',
  total: '[js-bulk-order="total"]',
}

/**
 * Global variables.
 */
let productData = null
let swatchData = null
let discountData = null

let items = []
let itemCount = 0
let subtotal = 0

let colorChoice = null
let firstChoice = false
let frameChoice = null
let quantityChoice = 1
let selectedVariant = null

/**
 * Export bulk order module.
 */
export default () => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    addCard: document.querySelector(selectors.addCard),
    addToCart: document.querySelector(selectors.addToCart),
    addToCartWrap: document.querySelector(selectors.addToCartWrap),
    addToOrder: document.querySelector(selectors.addToOrder),
    body: document.querySelector('body'),
    close: document.querySelector('[js-bulk-order="modal"] .icon-close'),
    colorSwatch: document.querySelectorAll(selectors.colorSwatch),
    colorSwatches: document.querySelector(selectors.colorSwatches),
    compareAt: document.querySelectorAll(selectors.compareAt),
    compareAtWrap: document.querySelector(selectors.compareAtWrap),
    discount: document.querySelector(selectors.discount),
    frameSwatch: document.querySelectorAll(selectors.frameSwatch),
    lensSwatch: document.querySelectorAll(selectors.lensSwatch),
    lensTooltip: document.querySelector(selectors.lensTooltip),
    modal: document.querySelector(selectors.modal),
    modalBackground: document.querySelector(selectors.modalBackground),
    modalInner: document.querySelector(selectors.modalInner),
    modalStep: document.querySelectorAll(selectors.modalStep),
    modalStep1: document.querySelector(selectors.modalStep1),
    productTrack: document.querySelector(selectors.productTrack),
    quantityDecrease: document.querySelectorAll(selectors.quantityDecrease),
    quantityIncrease: document.querySelectorAll(selectors.quantityIncrease),
    quantityInput: document.querySelectorAll(selectors.quantityInput),
    quantityModal: document.querySelectorAll(selectors.quantityModal),
    quantityWrap: document.querySelector(selectors.quantityWrap),
    sidebarEmpty: document.querySelector(selectors.sidebarEmpty),
    sidebarLineItems: document.querySelector(selectors.sidebarLineItems),
    sidebarLineItemsWrap: document.querySelector(selectors.sidebarLineItemsWrap),
    stepsWrap: document.querySelector(selectors.stepsWrap),
    subtotal: document.querySelector(selectors.subtotal),
    summaryColor: document.querySelector(selectors.summaryColor),
    summaryColorSwatch: document.querySelector(selectors.summaryColorSwatch),
    summaryImage: document.querySelector(selectors.summaryImage),
    summaryTitle: document.querySelector(selectors.summaryTitle),
    thresholdWarning: document.querySelector(selectors.thresholdWarning),
    total: document.querySelectorAll(selectors.total),
  }

  /**
   * Reset steps and choices when modal closes.
   */
  function resetSteps() {
    colorChoice = null
    frameChoice = null
    selectedVariant = null

    nodeSelectors.colorSwatches.innerHTML = ''
    nodeSelectors.summaryTitle.innerText = window.basis.strings.cart.selectShape
    getClearImage(productData[0])
    resetFrames()
    lockSteps()
  }

  /**
   * Toggle disabled state of quantity selectors in modal based on color choice.
   * @param {Boolean} bool - Whether or not to add disabled state.
   */
  function resetModalQuantities(bool) {
    if (bool) {
      nodeSelectors.quantityModal.forEach((element) => element.classList.add('is-disabled'))
      nodeSelectors.quantityWrap.classList.add('is-disabled')
      nodeSelectors.summaryColorSwatch.style = ''

    } else {
      nodeSelectors.quantityModal.forEach((element) => element.classList.remove('is-disabled'))
      nodeSelectors.quantityWrap.classList.remove('is-disabled')
    }
  }

  /**
   * Lock order steps after product added.
   */
  function lockSteps() {
    firstChoice = false

    nodeSelectors.modalStep.forEach((step) => step.classList.add('is-disabled'))
    resetModalQuantities(true)
    nodeSelectors.summaryImage.classList.add('is-disabled')
    nodeSelectors.addToOrder.classList.add('is-disabled')
  }

  /**
   * Unlock order steps after first frame choice made.
   */
  function unlockSteps() {
    firstChoice = true

    nodeSelectors.modalStep.forEach((step) => step.classList.remove('is-disabled'))
    nodeSelectors.summaryImage.classList.remove('is-disabled')
  }

  /**
   * Open ordering modal.
   */
  function openModal() {
    nodeSelectors.body.classList.add('modal-open')
    nodeSelectors.modal.classList.add('is-active')
    nodeSelectors.modalBackground.classList.add('is-active')
  }

  /**
   * Close ordering modal.
   */
  function closeModal() {
    nodeSelectors.modalInner.scrollTop = 0
    nodeSelectors.stepsWrap.scrollTop = 0
    nodeSelectors.body.classList.remove('modal-open')
    nodeSelectors.modal.classList.remove('is-active')
    nodeSelectors.modalBackground.classList.remove('is-active')
    resetQuantity()
    resetSteps()
  }

  /**
   * Check click location on modal to close on background click for desktop.
   * @param {Event} event - Click event.
   */
  function checkClickLocation(event) {
    if (nodeSelectors.modalInner.contains(event.target)) {
      return
    }

    closeModal()
  }

  /**
   * Get swatch data and format it for use in component.
   */
  function getSwatches() {
    fetch(theme.assets.swatches)
      .then((response) => response.json())
      .then((data) => {
        const array = Object.freeze(data.colors)

        swatchData = Object.keys(array).map((swatch) => {
          const key = Object.keys(array[swatch])[0]
          const value = Object.values(array[swatch])[0]

          return {
            key,
            value,
          }
        })

        nodeSelectors.modalStep1.classList.remove('is-disabled')
      })
  }

  /**
   * Set event listeners on color swatches after they are rendered.
   */
  function setColorSwatchListeners() {
    const swatches = document.querySelectorAll(selectors.colorSwatch)

    swatches.forEach((swatch) => {
      on('click', swatch, (event) => handleColorSwatchClick(event, swatches))
    })
  }

  /**
   * Get swatch style from swatchData, hex or .svg.
   * @param {String} color - Color choice.
   * @returns {String}
   */
  function getSwatchStyle(color) {
    const swatch = swatchData.find((obj) => obj.key === color)
    let style = `background-color: ${swatch.value};`

    if (swatch.value.includes('.svg')) {
      const url = `${window.theme.assets.base}${swatch.value}`

      style = `background-image: url(${url});`
    }

    return style
  }

  /**
   * Update swatches on variant change.
   * @param {String} title - Product title.
   */
  function updateSwatches(title) {
    if (!swatchData) {
      return
    }

    nodeSelectors.colorSwatches.innerHTML = ''

    const product = productData.find((obj) => obj.title === title)

    product.variants.forEach((variant) => {
      const colorOption = variant.selectedOptions.find((option) => option.name === 'Color')
      const colorName = colorOption.value.toLowerCase()
      const style = getSwatchStyle(colorName)

      const button = `
        <button
          class="bulk-order__color-swatch"
          style="${style}"
          data-color="${colorName}"
          data-title="${title}"
          js-bulk-order="colorSwatch"
        >
        </button>
      `

      nodeSelectors.colorSwatches.insertAdjacentHTML('beforeend', button)
    })

    setColorSwatchListeners()
  }

  /**
   * Helper function for fetching featured images.
   * @param {Boolean} bool - Whether or not to reset choices.
   * @param {Object} colorVariant - Variant to set selectedVariant to if found.
   */
  function resetModalChoices(bool, colorVariant) {
    if (bool) {
      selectedVariant = null
      nodeSelectors.addToOrder.classList.add('is-disabled')
      resetModalQuantities(true)

    } else {
      selectedVariant = colorVariant
      nodeSelectors.addToOrder.classList.remove('is-disabled')
      resetModalQuantities(false)
    }
  }

  /**
   * Get variant image and update summary pane.
   */
  function getFeaturedImage() {
    const product = productData.find((obj) => obj.title === frameChoice)
    let featuredImage = product.featuredImage.src

    if (!colorChoice) {
      resetModalChoices(true)

      const clearImage = product.variants.find((variant) => variant.title.includes('Clear'))

      if (!clearImage) {
        nodeSelectors.summaryImage.style.backgroundImage = `url(${featuredImage})`

        return
      }

      featuredImage = clearImage.originalSrc
      nodeSelectors.summaryImage.style.backgroundImage = `url(${featuredImage})`

      return
    }

    // eslint-disable-next-line max-len
    const colorVariant = product.variants.find((variant) => variant.title.toLowerCase().includes(colorChoice))

    if (!colorVariant) {
      resetModalChoices(true)
      nodeSelectors.summaryColor.innerText = window.basis.strings.cart.noColorFound
      getClearImage(product)

      return
    }

    const style = getSwatchStyle(colorChoice)

    resetModalChoices(false, colorVariant)
    featuredImage = selectedVariant.originalSrc
    nodeSelectors.summaryImage.style.backgroundImage = `url(${featuredImage})`
    nodeSelectors.summaryColor.innerText = colorVariant.title.split('/')[0].trim()
    nodeSelectors.summaryColorSwatch.style = style
  }

  /**
   * Get clear frame image for a product.
   * @param {Object} product - Product object.
   */
  function getClearImage(product) {
    let featuredImage = product.featuredImage.src
    const clearImage = product.variants.find((variant) => variant.title.includes('Clear'))

    if (!clearImage) {
      nodeSelectors.summaryImage.style.backgroundImage = `url(${featuredImage})`

      return
    }

    featuredImage = clearImage.originalSrc
    nodeSelectors.summaryImage.style.backgroundImage = `url(${featuredImage})`
  }

  /**
   * Toggle active state of color swatches and current value of color label.
   * @param {Event} event - Click event.
   * @param {Object} swatches - Array of color swatch elements.
   */
  function handleColorSwatchClick(event, swatches) {
    const swatch = event.currentTarget

    if (swatch.classList.contains('is-active')) {
      return
    }

    swatches.forEach((element) => element.classList.remove('is-active'))
    swatch.classList.add('is-active')

    colorChoice = swatch.dataset.color
    getFeaturedImage()
    resetQuantity()
  }

  /**
   * Remove active state of frame swatches and add default title.
   */
  function resetFrames() {
    nodeSelectors.frameSwatch.forEach((element) => element.classList.remove('is-active'))
    nodeSelectors.summaryColor.innerText = window.basis.strings.cart.noColor
  }

  /**
   * Toggle active state of frame swatches and current value of frame label.
   * @param {Event} event - Click event.
   */
  function handleFrameSwatchClick(event) {
    const swatch = event.currentTarget

    if (swatch.classList.contains('is-active')) {
      return
    }

    const { title } = swatch.dataset

    resetFrames()
    swatch.classList.add('is-active')
    nodeSelectors.summaryTitle.innerText = title

    colorChoice = null
    frameChoice = title
    getFeaturedImage()
    updateSwatches(title)
    resetQuantity()

    if (firstChoice) {
      return
    }

    unlockSteps()
  }

  /**
   * Reset quantity on new frame choice.
   */
  function resetQuantity() {
    quantityChoice = 1
    nodeSelectors.quantityInput.forEach((input) => input.setAttribute('value', 1))
    nodeSelectors.quantityDecrease.forEach((button) => button.classList.add('is-disabled'))
  }

  /**
   * Update quantity on decrease button click.
   */
  function handleQuantityDecrease() {
    quantityChoice -= 1
    nodeSelectors.quantityInput.forEach((input) => input.setAttribute('value', quantityChoice))
    nodeSelectors.quantityIncrease.forEach((button) => button.classList.remove('is-disabled'))

    if (quantityChoice > 1) {
      return
    }

    nodeSelectors.quantityDecrease.forEach((button) => button.classList.add('is-disabled'))
  }

  /**
   * Update quantity on increase button click.
   */
  function handleQuantityIncrease() {
    quantityChoice += 1
    nodeSelectors.quantityInput.forEach((input) => input.setAttribute('value', quantityChoice))
    nodeSelectors.quantityDecrease.forEach((button) => button.classList.remove('is-disabled'))

    if (selectedVariant.inventory > quantityChoice) {
      return
    }

    nodeSelectors.quantityIncrease.forEach((button) => button.classList.add('is-disabled'))
  }

  /**
   * Update quantity on line item decrease button click.
   * @param {Event} event - Click event.
   */
  function handleLineItemDecrease(event) {
    const button = event.currentTarget
    const cards = document.querySelectorAll(selectors.productCard)
    const matchingCards = [...cards].filter((card) => card.dataset.id === button.dataset.id)
    const productCard = matchingCards[0]
    const item = items.find((obj) => obj.id.toString() === productCard.dataset.id)

    item.quantity -= 1

    matchingCards.forEach((card) => {
      const decrease = card.querySelector(selectors.quantityDecreaseLineItem)
      const increase = card.querySelector(selectors.quantityIncreaseLineItem)
      const input = card.querySelector(selectors.quantityInputLineItem)

      increase.classList.remove('is-disabled')
      input.setAttribute('value', item.quantity)

      if (item.quantity > 1) {
        return
      }

      decrease.classList.add('is-disabled')
    })

    updateSubtotal()
  }

  /**
   * Update quantity on line item increase button click.
   * @param {Event} event - Click event.
   */
  function handleLineItemIncrease(event) {
    const button = event.currentTarget
    const cards = document.querySelectorAll(selectors.productCard)
    const matchingCards = [...cards].filter((card) => card.dataset.id === button.dataset.id)
    const productCard = matchingCards[0]
    const { id, inventory } = productCard.dataset
    const item = items.find((obj) => obj.id.toString() === id)

    item.quantity += 1

    matchingCards.forEach((card) => {
      const decrease = card.querySelector(selectors.quantityDecreaseLineItem)
      const increase = card.querySelector(selectors.quantityIncreaseLineItem)
      const input = card.querySelector(selectors.quantityInputLineItem)

      decrease.classList.remove('is-disabled')
      input.setAttribute('value', item.quantity)

      if (item.quantity < inventory) {
        return
      }

      increase.classList.add('is-disabled')
    })

    updateSubtotal()
  }

  /**
   * Set event listeners on line item quantity buttons after render.
   */
  function setQuantityListeners() {
    const quantityDecreaseLineItem = document.querySelectorAll(`.quantity__button--minus-${selectedVariant.id}`)
    const quantityIncreaseLineItem = document.querySelectorAll(`.quantity__button--plus-${selectedVariant.id}`)

    quantityDecreaseLineItem.forEach((button) => {
      on('click', button, (event) => handleLineItemDecrease(event))
    })

    quantityIncreaseLineItem.forEach((button) => {
      on('click', button, (event) => handleLineItemIncrease(event))
    })
  }

  /**
   * Set remove event listeners on product cards after render.
   */
  function setRemoveListeners() {
    const removeButtons = document.querySelectorAll(selectors.remove)

    removeButtons.forEach((button) => {
      on('click', button, () => {
        const cards = document.querySelectorAll(selectors.productCard)
        const parentCards = [...cards].filter((card) => card.dataset.id === button.dataset.id)

        items.forEach((item, index) => {
          if (item.id.toString() === button.dataset.id) {
            items.splice(index, 1)
            updateSubtotal()
          }
        })

        parentCards.forEach((card) => card.remove())

        const newCards = document.querySelectorAll(selectors.productCard)

        if (newCards.length) {
          return
        }

        nodeSelectors.sidebarEmpty.classList.add('is-active')
        nodeSelectors.sidebarLineItemsWrap.classList.add('is-disabled')
      })
    })
  }

  /**
   * Build quantity selectors for line item cards.
   * @param {String} modifiers - Additional classes for wrapper.
   * @param {String} selector - Javascript selector for wrapper.
   * @returns {String}
   */
  function buildQuantitySelector(modifiers, selector) {
    window.basis = window.basis || {}

    const { id, inventory } = selectedVariant
    let decreaseStyle = ''
    let increaseStyle = ''

    if (quantityChoice === 1) {
      decreaseStyle = 'is-disabled'
    }

    if (inventory === quantityChoice) {
      increaseStyle = 'is-disabled'
    }

    const strings = window.basis.strings
    const quantitySelector = `
      <div
        class="
          bulk-order__quantity-selector
          ${modifiers}
        "
        ${selector}
      >
        <span class="quantity_label sr-only">
          ${strings.cart.labelQuantity}
        </span>

        <div
          class="quantity__wrapper"
          data-quantity-selector
        >
          <button
            class="
              quantity__button
              quantity__button--minus
              quantity__button--minus-${id}
              ${decreaseStyle}
            "
            data-id="${id}"
            aria-label="${strings.cart.labelQuantityDecrease}"
            js-bulk-order="quantityDecreaseLineItem"
          >
            &minus;
          </button>

          <input
            aria-label="${strings.cart.labelQuantity}"
            class="quantity__input"
            type="number"
            min="1"
            size="4"
            pattern="[0-9]*"
            name="quantity"
            value="${quantityChoice}"
            js-bulk-order="quantityInputLineItem"
          >

          <button
            class="
              quantity__button
              quantity__button--plus
              quantity__button--plus-${id}
              ${increaseStyle}
            "
            data-id="${id}"
            aria-label="${strings.cart.labelQuantityIncrease}"
            js-bulk-order="quantityIncreaseLineItem"
          >
            +
          </button>
        </div>
      </div>
    `

    return quantitySelector
  }

  /**
   * Build product card for line items.
   * @returns {String}
   */
  function buildProductCard() {
    const {
      id,
      inventory,
      originalSrc,
      price,
      productTitle,
    } = selectedVariant

    const quantitySelector = buildQuantitySelector('line-item', selectors.quantityLineItem)
    const style = getSwatchStyle(colorChoice)

    const card = `
      <div
        class="bulk-order__product-card-wrap"
        data-id="${id}"
        data-inventory="${inventory}"
        data-price="${price.amount}"
        js-bulk-order="productCard"
      >
        <div
          class="bulk-order__product-card"
          style="background-image: url('${originalSrc}');"
        >
        </div>

        <div class="bulk-order__product-card-details">
          <div>
            <div class="bulk-order__product-card-title">
              <p class="bulk-order__summary-title">
                <span>${productTitle}</span>
              </p>

              <p>
                <span
                  class="bulk-order__summary-color"
                  style="${style}"
                ></span>

                <span class="bulk-order__summary-variant">
                  ${colorChoice} / Standard Lens
                </span>
              </p>
            </div>

            ${quantitySelector}
          </div>

          <button
            class="bulk-order__remove"
            data-id="${id}"
            js-bulk-order="remove"
          >
            ${icons.remove}
          </button>
        </div>
      </div>
    `

    return card
  }

  /**
   * Update Total after add, remove, increase and decrease events.
   * @param {Number} discountAmount - Amount to subtract from subtotal.
   */
  function updateTotal(discountAmount) {
    let total = subtotal / 100

    if (discountAmount > 0) {
      total = (subtotal / 100) - discountAmount
    }

    nodeSelectors.compareAtWrap.classList.toggle('is-disabled', discountAmount === 0)

    nodeSelectors.compareAt.forEach((element) => {
      element.innerText = (subtotal / 100).toFixed(2)
    })

    nodeSelectors.total.forEach((element) => {
      element.innerText = total.toFixed(2)
    })
  }

  /**
   * Update discounts after add, remove, increase and decrease events.
   */
  function updateDiscounts() {
    let discountThreshold = 0
    let matchingDiscount = null

    discountData.forEach((discount) => {
      if (itemCount >= discount.threshold && discount.threshold > discountThreshold) {
        discountThreshold = discount.threshold
        matchingDiscount = discount
      }
    })

    if (matchingDiscount === null) {
      nodeSelectors.discount.innerText = '0.00 (N/A)'
      nodeSelectors.addToCartWrap.classList.add('is-disabled')
      updateTotal(0)

      return
    }

    const percentage = matchingDiscount.discount / 100
    const amount = (subtotal / 100) * percentage

    nodeSelectors.discount.innerText = `${amount.toFixed(2)} (${matchingDiscount.discount}%)`
    nodeSelectors.addToCartWrap.classList.remove('is-disabled')
    updateTotal(amount)
  }

  /**
   * Update subtotal after add, remove, increase and decrease events.
   */
  function updateSubtotal() {
    subtotal = 0
    itemCount = 0

    items.forEach((item) => {
      const cards = document.querySelectorAll(selectors.productCard)
      const matchingCards = [...cards].filter((card) => card.dataset.id === item.id.toString())
      const productCard = matchingCards[0]
      const input = productCard.querySelector(selectors.quantityInputLineItem)

      subtotal += parseInt(productCard.dataset.price) * parseInt(input.value)
      itemCount += item.quantity
    })

    let subTotalString = 0

    if (subtotal > 0) {
      subTotalString = subtotal / 100
    }

    nodeSelectors.subtotal.innerText = subTotalString.toFixed(2)
    updateDiscounts()
  }

  /**
   * Check for matching variant in order.
   * @returns {Boolean}
   */
  function hasMatchingVariant() {
    const matchingVariant = items.find((item) => item.id === selectedVariant.id)

    if (matchingVariant) {
      return true
    }

    return false
  }

  /**
   * Update items array.
   * @param {Boolean} match - If variant is already in array.
   */
  function updateItems(match) {
    if (!match) {
      items.push({
        id: selectedVariant.id,
        quantity: quantityChoice,
      })

      return
    }

    const matchingVariant = items.find((item) => item.id === selectedVariant.id)
    const cards = document.querySelectorAll(selectors.productCard)
    const id = selectedVariant.id.toString()
    const matchingCards = [...cards].filter((card) => card.dataset.id === id)

    matchingVariant.quantity += quantityChoice

    matchingCards.forEach((card) => {
      const input = card.querySelector(selectors.quantityInputLineItem)

      input.setAttribute('value', matchingVariant.quantity)
    })
  }

  /**
   * Handle click on "add to order" button.
   */
  function handleAddProduct() {
    const productCard = buildProductCard()
    const match = hasMatchingVariant()

    if (!match) {
      nodeSelectors.productTrack.insertAdjacentHTML('afterbegin', productCard)
      nodeSelectors.sidebarLineItems.insertAdjacentHTML('beforeend', productCard)
      setRemoveListeners()
      setQuantityListeners()
    }

    updateItems(match)
    updateSubtotal()
    nodeSelectors.sidebarEmpty.classList.remove('is-active')
    nodeSelectors.sidebarLineItemsWrap.classList.remove('is-disabled')
    closeModal()
  }

  /**
   * Reset to original state after items added to cart.
   */
  function resetModule() {
    const cartDrawer = document.querySelector('[data-drawer="drawer-cart"]')
    const productCard = document.querySelectorAll(selectors.productCard)

    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }))
    cartDrawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }))

    items = []
    itemCount = 0
    subtotal = 0

    productCard.forEach((card) => card.remove())
    nodeSelectors.sidebarEmpty.classList.add('is-active')
    nodeSelectors.sidebarLineItemsWrap.classList.add('is-disabled')
    nodeSelectors.addToCart.classList.remove('is-disabled')
    nodeSelectors.addToCart.innerText = window.basis.strings.cart.addToCart
  }

  /**
   * Update cart note after products added successfully.
   */
  async function updateCartNote() {
    const body = JSON.stringify({
      note: 'Products added from the bulk order module.',
    })

    await fetch('/cart/update.js', {
      body,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      method: 'POST',
    })
      .catch((err) => {
        console.error(err)
      })
  }

  /**
   * Callback after successful add to cart.
   */
  function onSuccess() {
    fetch(`${window.theme.routes.cart}.js`)
      .then((response) => {
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', {
            detail: {
              cart: response.data,
            },
            bubbles: true,
          }),
        )

        resetModule()
        updateCartNote()
      })
      .catch((error) => {
        console.error(error)
      })
  }

  /**
   * Add one or more items to cart.
   */
  async function handleAddToCart() {
    const body = JSON.stringify({ items })

    nodeSelectors.addToCart.classList.add('is-disabled')
    nodeSelectors.addToCart.innerText = window.basis.strings.cart.addingToCart

    await fetch('/cart/add.js', {
      body,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      method: 'POST',
    })
      .then(() => {
        onSuccess()
      })
      .catch((err) => {
        console.error(err)
      })
  }

  /**
   * Set event listeners.
   */
  function setEventListeners() {
    on('click', nodeSelectors.addCard, () => openModal())
    on('click', nodeSelectors.close, () => closeModal())
    on('click', nodeSelectors.modal, (event) => checkClickLocation(event))

    nodeSelectors.frameSwatch.forEach((swatch) => {
      on('click', swatch, (event) => handleFrameSwatchClick(event))
    })

    nodeSelectors.lensSwatch.forEach((swatch) => {
      on('click', swatch, () => nodeSelectors.lensTooltip.classList.toggle('is-active'))

      if (window.innerWidth < breakpoints.l) {
        return
      }

      on('mouseenter', swatch, () => nodeSelectors.lensTooltip.classList.add('is-active'))
      on('mouseleave', swatch, () => nodeSelectors.lensTooltip.classList.remove('is-active'))
    })

    nodeSelectors.quantityDecrease.forEach((button) => {
      on('click', button, () => handleQuantityDecrease())
    })

    nodeSelectors.quantityIncrease.forEach((button) => {
      on('click', button, () => handleQuantityIncrease())
    })

    on('click', nodeSelectors.addToOrder, () => handleAddProduct())
    on('click', nodeSelectors.addToCart, () => handleAddToCart())
  }

  /**
   * Find the lowest number of units that qualifies for a discount.
   */
  function setMinThreshold() {
    if (!discountData.length) {
      return
    }

    const array = discountData
    const warning = window.basis.strings.cart.thresholdWarning

    array.sort((a, b) => {
      return a.threshold - b.threshold
    })

    nodeSelectors.thresholdWarning.innerText = warning.replace('{{ x }}', array[0].threshold)
  }

  /**
   * Query collection for product data.
   * @param {String} handle - Collection handle.
   * @param {Number} first - Number of product results to return.
   */
  async function queryCollection(handle, first = 50) {
    const { country, language } = basis.store

    try {
      const response = await basis.Query({
        locale: language,
        query: collection,
        variables: {
          country,
          first,
          handle,
        },
      })

      const collectionFormatted = formatBulkOrderCollection(response)

      productData = Object.freeze(collectionFormatted.products)
      discountData = window.bulkOrderProgress.discounts
      getClearImage(productData[0])
      getSwatches()
      setMinThreshold()

    } catch (error) {
      console.log(error)
    }
  }

  /**
   * Initialize component.
   */
  function init() {
    if (!window.Shopify.bulkOrderCollection.length) {
      return
    }

    queryCollection(window.Shopify.bulkOrderCollection)
    setEventListeners()
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
